import { useCallback, useEffect, useState } from "react";

type Stato = Record<string, unknown> | null;

/**
 * Una vista «di dettaglio» sul telefono (conversazione, scheda magazzino) che
 * il tasto indietro di Android deve chiudere invece di uscire dalla pagina.
 *
 * Aprirla aggiunge un passo alla cronologia con lo STESSO URL: nello `state`
 * c'è solo un segno (`chiave`), copiato sopra quello del router così
 * react-router non vede un cambio di pagina. Il «indietro» toglie il passo e
 * il `popstate` chiude la vista; il tasto «‹» della pagina fa lo stesso.
 *
 * `attivo` = false (computer): niente passi, `apri`/`chiudi` cambiano solo lo
 * stato.
 */
export function usePassoIndietro(attivo: boolean, chiave: string) {
  const [aperto, setAperto] = useState(false);

  /** `passo: false` quando la vista nasce aperta da un deep-link: non c'è una
   *  lista «prima» a cui tornare con la cronologia. */
  const apri = useCallback(
    ({ passo = true }: { passo?: boolean } = {}) => {
      setAperto(true);
      if (attivo && passo) {
        const stato = (window.history.state ?? {}) as Record<string, unknown>;
        window.history.pushState({ ...stato, [chiave]: true }, "");
      }
    },
    [attivo, chiave],
  );

  const chiudi = useCallback(() => {
    if ((window.history.state as Stato)?.[chiave]) {
      window.history.back(); // il popstate qui sotto chiude la vista
      return;
    }
    setAperto(false);
  }, [chiave]);

  useEffect(() => {
    if (!attivo) return;
    const onPop = () => {
      if (!(window.history.state as Stato)?.[chiave]) setAperto(false);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [attivo, chiave]);

  return { aperto, apri, chiudi };
}
